import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { collection, doc, onSnapshot, orderBy, query, setDoc } from 'firebase/firestore';
import { COL, formatMinor, normaliseCode, toMajor, toMinor, type Coupon } from '@eprintsmart/shared';
import { db } from '../firebase';

/**
 * Coupon management.
 *
 * Codes were previously accepted at checkout, stored on the order and never
 * applied — a customer could type anything and be charged full price. This is
 * the other half of that fix.
 */
export function Coupons() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<Coupon> | null>(null);

  useEffect(
    () =>
      onSnapshot(query(collection(db, COL.coupons), orderBy('createdAt', 'desc')), (snap) => {
        setCoupons(snap.docs.map((d) => ({ ...(d.data() as Coupon), code: d.id })));
        setLoading(false);
      }),
    [],
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h1" sx={{ flexGrow: 1 }}>Coupons</Typography>
        <Button
          variant="contained"
          onClick={() => setEditing({ type: 'PERCENT', value: 10, status: 'ACTIVE', redemptions: 0 })}
        >
          New coupon
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
      ) : coupons.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No coupons yet.</Typography>
        </Paper>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Min spend</TableCell>
                <TableCell>Cap</TableCell>
                <TableCell align="right">Used</TableCell>
                <TableCell>Valid until</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.map((c) => (
                <TableRow key={c.code} hover sx={{ cursor: 'pointer' }} onClick={() => setEditing(c)}>
                  <TableCell><strong>{c.code}</strong>
                    {c.description && <Typography variant="caption" display="block" color="text.secondary">{c.description}</Typography>}
                  </TableCell>
                  <TableCell>{c.type === 'PERCENT' ? `${c.value}%` : formatMinor(c.value)}</TableCell>
                  <TableCell>{c.minSpendMinor ? formatMinor(c.minSpendMinor) : '—'}</TableCell>
                  <TableCell>{c.maxDiscountMinor ? formatMinor(c.maxDiscountMinor) : '—'}</TableCell>
                  <TableCell align="right">
                    {c.redemptions}{c.maxRedemptions ? ` / ${c.maxRedemptions}` : ''}
                  </TableCell>
                  <TableCell>
                    {c.validUntil ? new Date(c.validUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={c.status}
                      color={c.status === 'ACTIVE' ? 'success' : 'default'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <CouponDialog draft={editing} onClose={() => setEditing(null)} />
    </Stack>
  );
}

function CouponDialog({ draft, onClose }: { draft: Partial<Coupon> | null; onClose: () => void }) {
  const [form, setForm] = useState<Partial<Coupon>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(draft ?? {}); setError(null); }, [draft]);
  if (!draft) return null;

  const isNew = !draft.createdAt;
  const set = <K extends keyof Coupon>(k: K, v: Coupon[K]) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const code = normaliseCode(form.code ?? '');
    if (code.length < 3) return setError('A code of at least 3 characters is required.');
    if (!(Number(form.value) > 0)) return setError('The discount must be greater than zero.');
    if (form.type === 'PERCENT' && Number(form.value) > 90) {
      return setError('A percentage above 90% is almost certainly a mistake.');
    }

    setSaving(true);
    try {
      await setDoc(doc(db, COL.coupons, code), {
        ...form,
        code,
        redemptions: form.redemptions ?? 0,
        updatedAt: Date.now(),
        ...(isNew ? { createdAt: Date.now() } : {}),
      }, { merge: true });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const dateValue = (ms?: number) => (ms ? new Date(ms).toISOString().slice(0, 10) : '');

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'New coupon' : form.code}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={2}>
            <TextField label="Code" value={form.code ?? ''} disabled={!isNew}
              onChange={(e) => set('code', normaliseCode(e.target.value))} sx={{ flex: 1 }}
              helperText={isNew ? 'Case-insensitive' : 'Fixed after creation'} />
            <TextField label="Type" select value={form.type ?? 'PERCENT'}
              onChange={(e) => set('type', e.target.value as Coupon['type'])} sx={{ width: 160 }}>
              <MenuItem value="PERCENT">Percentage</MenuItem>
              <MenuItem value="FLAT">Flat amount</MenuItem>
            </TextField>
          </Stack>

          <TextField
            label={form.type === 'FLAT' ? 'Discount (₹)' : 'Discount (%)'}
            type="number"
            value={form.type === 'FLAT' ? toMajor(form.value ?? 0) : (form.value ?? 0)}
            onChange={(e) => set('value', form.type === 'FLAT' ? toMinor(Number(e.target.value)) : Number(e.target.value))}
          />

          <TextField label="Description" value={form.description ?? ''}
            onChange={(e) => set('description', e.target.value)}
            helperText="Internal only — customers never see this." />

          <Stack direction="row" spacing={2}>
            <TextField label="Minimum spend (₹)" type="number"
              value={form.minSpendMinor ? toMajor(form.minSpendMinor) : ''}
              onChange={(e) => set('minSpendMinor', e.target.value ? toMinor(Number(e.target.value)) : undefined)}
              sx={{ flex: 1 }} />
            <TextField label="Maximum discount (₹)" type="number"
              value={form.maxDiscountMinor ? toMajor(form.maxDiscountMinor) : ''}
              onChange={(e) => set('maxDiscountMinor', e.target.value ? toMinor(Number(e.target.value)) : undefined)}
              sx={{ flex: 1 }}
              helperText="Caps a percentage on a large order" />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField label="Total redemptions" type="number"
              value={form.maxRedemptions ?? ''}
              onChange={(e) => set('maxRedemptions', e.target.value ? Number(e.target.value) : undefined)}
              sx={{ flex: 1 }} helperText="Blank for unlimited" />
            <TextField label="Per customer" type="number"
              value={form.maxPerCustomer ?? ''}
              onChange={(e) => set('maxPerCustomer', e.target.value ? Number(e.target.value) : undefined)}
              sx={{ flex: 1 }} helperText="Needs a signed-in customer" />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField label="Valid from" type="date" InputLabelProps={{ shrink: true }}
              value={dateValue(form.validFrom)}
              onChange={(e) => set('validFrom', e.target.value ? new Date(e.target.value).getTime() : undefined)}
              sx={{ flex: 1 }} />
            <TextField label="Valid until" type="date" InputLabelProps={{ shrink: true }}
              value={dateValue(form.validUntil)}
              onChange={(e) => set('validUntil', e.target.value ? new Date(`${e.target.value}T23:59:59`).getTime() : undefined)}
              sx={{ flex: 1 }} />
            <TextField label="Status" select value={form.status ?? 'ACTIVE'}
              onChange={(e) => set('status', e.target.value as Coupon['status'])} sx={{ width: 150 }}>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="PAUSED">Paused</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
            </TextField>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Save coupon</Button>
      </DialogActions>
    </Dialog>
  );
}
